import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '@signalvote/api-client';
import { queryKeys } from '@/lib/queryKeys';
import { useCampaignStore } from '@/stores';

const api = getApiClient();

/** Fetch visible categories (defaults + non-hidden custom with access) */
export function useTaskCategories() {
  const { selectedCampaign } = useCampaignStore();

  return useQuery({
    queryKey: queryKeys.taskCategories.visible(),
    queryFn: () => api.taskCategories.getCategories(),
    enabled: !!selectedCampaign,
  });
}

/** Create a custom category */
export function useCreateCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (name: string) => api.taskCategories.createCategory({ name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.taskCategories.all });
    },
  });
}

/** Hide a custom category */
export function useHideCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.taskCategories.hideCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.taskCategories.all });
    },
  });
}

/** Rename a custom category */
export function useRenameCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.taskCategories.renameCategory(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.taskCategories.all });
      // Tasks display category name, so refresh those too
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
}

/** Delete a custom category */
export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.taskCategories.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.taskCategories.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
}
